// Public audiobook player — reached via a share link (/#listen?t=TOKEN).
// Renders before the auth gate: anyone with the link can listen, nothing else.

import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api';
import type { PublicAudiobook } from '../lib/api';
import { Icon, Spinner, Waveform } from '../components/ui';

function formatDuration(seconds: number | null | undefined): string {
  if (!seconds || !Number.isFinite(seconds)) return '';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
}

export function Listen() {
  const token = useMemo(() => {
    // Current share links: /listen/<token> (path form, enables link previews)
    const pathMatch = window.location.pathname.match(/^\/listen\/([A-Za-z0-9]+)/);
    if (pathMatch) return pathMatch[1];
    // Older share links: /#listen?t=<token>
    const query = window.location.hash.split('?')[1] || '';
    return new URLSearchParams(query).get('t') || '';
  }, []);

  const [book, setBook] = useState<PublicAudiobook | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  // Autoplay only after the listener has interacted (chapter click / ended)
  const autoplayRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setError('This listening link is incomplete — ask for a fresh one.');
      setLoading(false);
      return;
    }
    api.getPublicAudiobook(token).then((res) => {
      if (res.success && res.data) {
        setBook(res.data);
        document.title = `${res.data.name} — Audiaro`;
      } else {
        setError('This audiobook is no longer shared or the link is invalid.');
      }
      setLoading(false);
    });
  }, [token]);

  // Restore the platform title if the user navigates away from the player
  useEffect(() => {
    const original = document.title;
    return () => {
      document.title = original;
    };
  }, []);

  const chapters = book?.chapters || [];
  const chapter = chapters[current];

  useEffect(() => {
    if (autoplayRef.current && audioRef.current) {
      audioRef.current.play().catch(() => {});
    }
  }, [current]);

  const selectChapter = (i: number) => {
    autoplayRef.current = true;
    if (i === current) {
      const audio = audioRef.current;
      if (!audio) return;
      if (audio.paused) audio.play().catch(() => {});
      else audio.pause();
    } else {
      setCurrent(i);
    }
  };

  const step = (dir: -1 | 1) => {
    const next = current + dir;
    if (next >= 0 && next < chapters.length) {
      autoplayRef.current = true;
      setCurrent(next);
    }
  };

  const onEnded = () => {
    if (current < chapters.length - 1) {
      autoplayRef.current = true;
      setCurrent(current + 1);
    } else {
      setPlaying(false);
    }
  };

  const totalDuration = chapters.reduce((sum, ch) => sum + (ch.duration_seconds || 0), 0);

  return (
    <div className="min-h-screen bg-ink-950 text-white flex flex-col items-center px-5 py-10">
      <div className="w-full max-w-[680px] flex flex-col gap-5">
        {/* Brand */}
        <div className="flex items-center justify-center gap-2 text-mist-500 text-[12.5px]">
          <Icon name="generate" size={16} />
          <span className="font-display font-bold tracking-wide">Audiaro</span>
          <span>· shared audiobook</span>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-24">
            <Spinner size={34} />
          </div>
        )}

        {!loading && error && (
          <div className="card p-8 text-center">
            <span className="inline-flex text-mist-500">
              <Icon name="book" size={36} />
            </span>
            <p className="mt-4 m-0 text-mist-300 text-[14.5px]">{error}</p>
          </div>
        )}

        {!loading && book && (
          <>
            {/* Book header */}
            <div className="card p-6 text-center">
              <span
                className="inline-flex w-14 h-14 rounded-2xl items-center justify-center"
                style={{ background: 'rgba(var(--a-rgb), .14)', color: 'var(--a-hi)' }}
              >
                <Icon name="book" size={26} />
              </span>
              <h1 className="font-display font-extrabold text-[22px] m-0 mt-3">{book.name}</h1>
              {book.description && <p className="m-0 mt-1.5 text-mist-400 text-[13.5px]">{book.description}</p>}
              <p className="m-0 mt-2 text-mist-500 text-[12.5px]">
                {chapters.length} chapter{chapters.length === 1 ? '' : 's'}
                {totalDuration > 0 && <> · {formatDuration(totalDuration)}</>}
                {book.language && <> · {book.language}</>}
              </p>
            </div>

            {chapters.length === 0 ? (
              <div className="card p-8 text-center text-mist-400 text-[13.5px]">
                No chapters are ready to play yet — check back soon.
              </div>
            ) : (
              <>
                {/* Now playing */}
                <div
                  className="p-[22px] rounded-2xl"
                  style={{
                    border: '1px solid var(--a-core)',
                    background: 'linear-gradient(180deg, rgba(var(--a-rgb), .08), rgba(23,23,27,0))',
                  }}
                >
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => step(-1)}
                      disabled={current === 0}
                      aria-label="Previous chapter"
                      className="text-mist-400 hover:text-white disabled:opacity-25"
                    >
                      <Icon name="chevronUp" size={20} className="-rotate-90" />
                    </button>
                    <div className="flex-1 min-w-0 text-center">
                      <p className="m-0 text-[11.5px] uppercase tracking-wider text-mist-500">
                        Chapter {chapter.idx} of {chapters.length}
                      </p>
                      <p className="m-0 mt-0.5 text-white text-[15px] font-semibold truncate">{chapter.title}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => step(1)}
                      disabled={current === chapters.length - 1}
                      aria-label="Next chapter"
                      className="text-mist-400 hover:text-white disabled:opacity-25"
                    >
                      <Icon name="chevronDown" size={20} className="-rotate-90" />
                    </button>
                  </div>
                  <audio
                    ref={audioRef}
                    key={chapter.public_url}
                    controls
                    src={chapter.public_url}
                    onPlay={() => setPlaying(true)}
                    onPause={() => setPlaying(false)}
                    onEnded={onEnded}
                    className="w-full mt-4"
                    style={{ height: 40 }}
                  />
                  <p className="m-0 mt-2 text-center text-mist-500 text-[11.5px]">
                    Chapters play in order automatically.
                  </p>
                </div>

                {/* Chapter list */}
                <div className="card p-[18px] flex flex-col gap-2">
                  {chapters.map((ch, i) => {
                    const active = i === current;
                    return (
                      <button
                        key={ch.idx}
                        type="button"
                        onClick={() => selectChapter(i)}
                        className={`flex items-center gap-3.5 px-4 py-3 rounded-[13px] border text-left cursor-pointer transition-colors ${
                          active ? 'border-[var(--a-core)] bg-ink-950' : 'border-ink-800 bg-ink-900 hover:border-ink-600'
                        }`}
                      >
                        <span className="w-7 flex-none text-center font-mono text-[13px] text-mist-500">{ch.idx}</span>
                        <span
                          className="w-9 h-9 flex-none flex items-center justify-center rounded-[9px] border border-ink-600 bg-ink-950"
                        >
                          <Icon name={active && playing ? 'pause' : 'play'} size={14} fill="var(--a-core)" />
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-white text-[14px] font-semibold truncate">{ch.title}</span>
                          <span className="mt-[5px] h-4 overflow-hidden hidden sm:block">
                            <Waveform seed={ch.title || String(ch.idx)} active={active && playing} />
                          </span>
                        </span>
                        <span className="flex-none font-mono text-[12.5px] text-mist-500">
                          {formatDuration(ch.duration_seconds)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            <p className="m-0 text-center text-mist-600 text-[11.5px]">
              Shared for listening via Audiaro — this link only plays the audio.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
